import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('adminPanelDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem('adminPanelDarkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: isDarkMode ? '#90caf9' : '#1976d2',
      },
      secondary: {
        main: isDarkMode ? '#f48fb1' : '#dc004e',
      },
      background: {
        default: isDarkMode ? '#121212' : '#f5f5f5',
        paper: isDarkMode ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#000000',
        secondary: isDarkMode ? '#b0b0b0' : '#666666',
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
            border: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
            color: isDarkMode ? '#ffffff' : '#000000',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              color: isDarkMode ? '#ffffff' : '#000000',
              borderBottom: isDarkMode ? '1px solid #444' : '1px solid #e0e0e0',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              color: isDarkMode ? '#ffffff' : '#000000',
              flexGrow: 1,
              '&:hover': {
                backgroundColor: isDarkMode ? '#3a3a3a' : '#e8e8e8',
              },
            },
            '& .MuiDataGrid-cell': {
              backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000',
              borderBottom: isDarkMode ? '1px solid #333' : '1px solid #e0e0e0',
              flexGrow: 1,
            },
            '& .MuiDataGrid-row': {
              backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
              '&:nth-of-type(odd)': {
                backgroundColor: isDarkMode ? '#2a2a2a' : '#fafafa',
              },
              '&:hover': {
                backgroundColor: isDarkMode ? '#3a3a3a' : '#f0f0f0',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              color: isDarkMode ? '#ffffff' : '#000000',
              borderTop: isDarkMode ? '1px solid #444' : '1px solid #e0e0e0',
            },
            '& .MuiDataGrid-virtualScroller': {
              width: '100%',
            },
            '& .MuiDataGrid-main': {
              width: '100%',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: isDarkMode ? '#90caf9' : '#1976d2',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.08)',
              },
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: isDarkMode ? '#90caf9' : '#1976d2',
            },
            '& .MuiSwitch-switchBase': {
              color: isDarkMode ? '#666' : '#ccc',
            },
            '& .MuiSwitch-track': {
              backgroundColor: isDarkMode ? '#444' : '#ccc',
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
            '& .MuiTableCell-head': {
              backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
              color: isDarkMode ? '#ffffff' : '#000000',
              fontWeight: 'bold',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: isDarkMode ? '#2d2d2d' : '#f5f5f5',
            color: isDarkMode ? '#ffffff' : '#000000',
            fontWeight: 'bold',
          },
          body: {
            backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
            color: isDarkMode ? '#ffffff' : '#000000',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:nth-of-type(odd)': {
              backgroundColor: isDarkMode ? '#2a2a2a' : '#fafafa',
            },
            '&:hover': {
              backgroundColor: isDarkMode ? '#3a3a3a' : '#f0f0f0',
            },
          },
        },
      },
    },
  });

  const value = {
    isDarkMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}; 